import React, { useState, useEffect, ChangeEvent } from "react";
import { Form, Button, Card, Alert, Spinner, Col, Row } from "react-bootstrap";
import { doc, getDoc, setDoc } from "firebase/firestore";
import MainContainer from "../common/MainContainer";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { CountryCodes } from "../../model/UserModel";
import { useGlobal } from "../../contexts/GlobalContext";
import { maindb, storage } from "../../firebaseConfig";
import { Utils } from "../../common/Utils";
import { updatePassword } from "firebase/auth";
import PasswordInput from "../common/PasswordInput";
import { MessageType } from "../../gateways/MessageGateway";

interface ProfileData {
    firstName: string;
    lastName: string;
    phoneCode: string;
    phoneNumber: string;
    profileImage: string;
    messagePreferenceVerified: boolean;
    messagePreference: MessageType;
}

const UserProfile = () => {

    const [profileData, setProfileData] = useState<ProfileData>({ firstName: "", lastName: "", phoneCode: "+27", phoneNumber: "", profileImage: "", messagePreferenceVerified: false, messagePreference: MessageType.Email });
    const [image, setImage] = useState<File | null>(null);
    const [initialPhoneNumber, setInitialPhoneNumber] = useState<string>("");
    const [saving, setSaving] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [newPassword, setNewPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [saveText, setSaveText] = useState<string>("");
    const [errors, setErrors] = useState<{ firstName?: string, lastName?: string, confirmPassword?: string; password?: string; phoneNumber?: string, globalError?: string }>({});
    const { user } = useGlobal();

    useEffect(() => {

        if (user) {
            const fetchUserData = async () => {
                const userRef = doc(maindb, "users", user.uid);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    const userDetails = {
                        firstName: userSnap.data().firstName,
                        lastName: userSnap.data().lastName,
                        phoneCode: userSnap.data().phoneCode ?? "+27",
                        phoneNumber: userSnap.data().phoneNumber,
                        profileImage: userSnap.data().profileImage,
                        messagePreferenceVerified: userSnap.data().messagePreferenceVerified,
                        messagePreference: userSnap.data()?.messagePreference
                    };
                    setInitialPhoneNumber(Utils.FormatPhoneNumber(userDetails.phoneCode, userDetails.phoneNumber));
                    setProfileData(userDetails);
                }
                setLoading(false);
            };
            fetchUserData();
        }
    }, [user]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setProfileData({ ...profileData, [e.target.name]: e.target.value });
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setProfileData({ ...profileData, [e.target.name]: e.target.value });
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setImage(e.target.files[0]);
        }
    };

    const handleSave = async () => {

        let newErrors: { firstName?: string, lastName?: string, email?: string; confirmPassword?: string; password?: string, phoneNumber?: string, globalError?: string } = {};

        if (profileData.firstName === "") {
            newErrors.firstName = "First Name is required";
        }
        if (profileData.lastName === "") {
            newErrors.lastName = "Last Name is required";
        }

        if (!profileData.phoneNumber.trim()) {
            newErrors.phoneNumber = "Phone number is required";
        }
        else if (!profileData.phoneNumber.match(/^\d{2,3}\d{3}\d{4}$/)) {
            newErrors.phoneNumber = "Invalid phone number (valid e.g 0821101010)";
        }

        if (newPassword || confirmPassword) {
            if (newPassword.length < 6) {
                newErrors.password = "Password must be at least 6 characters long";
            }

            if (confirmPassword !== newPassword) {
                newErrors.confirmPassword = "Passwords do not match";
            }
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0 && user) {
            try {
                setSaving(true);
                let imageUrl = profileData.profileImage;

                // Upload new profile image if selected
                if (image) {
                    const imageRef = ref(storage, `profileImages/${user.uid}`);
                    await uploadBytes(imageRef, image);
                    imageUrl = await getDownloadURL(imageRef);
                }

                // Save updated profile data to Firestore
                await setDoc(doc(maindb, "users", user.uid), {
                    ...profileData, phoneNumber: profileData.phoneNumber ?? '', profileImage: imageUrl ?? '',
                    messagePreferenceVerified: profileData.messagePreferenceVerified && (profileData.messagePreference === MessageType.Email || initialPhoneNumber === Utils.FormatPhoneNumber(profileData.phoneCode, profileData.phoneNumber))
                }, { merge: true });

                // Update state with new image URL
                setProfileData(prev => ({ ...prev, profileImage: imageUrl }));

                try {

                    if (newPassword || confirmPassword) {
                        await updatePassword(user, newPassword);
                    }
                } catch (err) {
                    console.error("Error updating password", err);
                    newErrors.globalError = "Failed to update password. Password reset requires recent login.";
                    setErrors(newErrors);
                    return;
                }
                setSaveText('Saved Successfully');
            }
            catch (err) {
                console.error("Error saving user data", err);
                newErrors.globalError = "Error saving user data";
                setErrors(newErrors);
            }
            finally {
                setSaving(false);
                setTimeout(() => {
                    setSaveText('');
                    setLoading(false);
                }, 2000);
            }
        }
    };

    return (
        <MainContainer>
            <h2 className="mb-4">User Profile</h2>
            {user ? (loading ? (<Spinner animation="border" />) :
                (<>
                    <div className="profile-image-container">
                        <Card.Img variant="top" src={profileData.profileImage || "/images/icons//profile.png"} className="profile-image rounded-circle mx-auto d-block" />
                    </div>
                    {errors.globalError && <Alert variant="danger mt-3">{errors.globalError}</Alert>}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Profile Image</Form.Label>
                            <Form.Control type="file" onChange={handleImageChange} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="firstName"
                                value={profileData.firstName}
                                isInvalid={!!errors.firstName}
                                onChange={handleChange} />
                            <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control type="text" name="lastName" isInvalid={!!errors.lastName} value={profileData.lastName} onChange={handleChange} />
                            <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Cellphone Number</Form.Label>
                            <Row className="g-2">
                                <Col xs={12} md={4}>
                                    <Form.Select
                                        value={profileData.phoneCode}
                                        name="phoneCode"
                                        onChange={handleSelectChange}
                                    >
                                        {CountryCodes.map((country, index) => (
                                            <option key={index} value={country.code}>
                                                {country.name} ({country.code})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col xs={12} md={8}>
                                    <Form.Control
                                        type="text"
                                        name="phoneNumber"
                                        value={profileData.phoneNumber}
                                        isInvalid={!!errors.phoneNumber}
                                        onChange={handleChange}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.phoneNumber}
                                    </Form.Control.Feedback>
                                </Col>
                            </Row>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <PasswordInput
                                value={newPassword}
                                onChange={(e) => setNewPassword(e)}
                                placeholder="Enter new password"
                                label="New Password"
                                isInvalid={!!errors.password}
                                feedback={errors.password}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Confirm Password</Form.Label>
                            <Form.Control
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                isInvalid={!!errors.confirmPassword}
                            />
                            <Form.Control.Feedback type="invalid">{errors.confirmPassword}</Form.Control.Feedback>
                        </Form.Group>

                        <Button className="mt-3" variant="primary" onClick={handleSave} disabled={saving}>
                            {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
                        </Button>
                        {saveText && <Alert variant="success" className="mb-0 mt-1 py-1 px-2">
                            {saveText}
                        </Alert>}
                    </Form>
                </>)
            ) : (
                <p>User is not loaded. Please ensure authentication happens on the login page.</p>
            )}
        </MainContainer>
    );
};

export default UserProfile;